import React from 'react';
import { AlertTriangle, CheckCircle2, Clock, Info, XCircle } from 'lucide-react';

import { AGING_FILTER_VALUES, STATUS_FILTER_VALUES } from 'components/logistics/fulfillmentLifecycle/constants';
import { Badge } from 'shadcn/components/ui/Badge';
import { Tooltip, TooltipContent, TooltipTrigger } from 'shadcn/components/ui/tooltip';
import { cn } from 'shadcn/lib/utils';

/**
 * RiskSummaryBadge
 *
 * Clickable summary badge that toggles the dashboard status filter.
 */
function RiskSummaryBadge({
    baseClassName,
    count,
    countClassName,
    focusClassName,
    hoverClassName,
    icon: Icon,
    iconClassName,
    label,
    labelClassName,
    onStatusFilterClick,
    selectedClassName,
    status,
    statusFilter,
}) {
    const isSelected = statusFilter === status;

    return (
        <button
            type="button"
            onClick={() => onStatusFilterClick(status)}
            aria-pressed={isSelected}
            className={cn(
                'inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                baseClassName,
                hoverClassName,
                focusClassName,
                isSelected && selectedClassName,
            )}
        >
            <Icon className={cn('h-5 w-5', iconClassName)} aria-hidden="true" />
            <span className={cn('text-2xl font-bold', countClassName)}>{count}</span>
            <span className={cn('text-sm font-medium', labelClassName)}>{label}</span>
        </button>
    );
}

/**
 * AgingSummaryBadge
 *
 * Clickable summary badge that toggles one aging filter option (multi-select).
 */
function AgingSummaryBadge({
    agingFilters,
    agingType,
    baseClassName,
    count,
    countClassName,
    focusClassName,
    hoverClassName,
    icon: Icon,
    iconClassName,
    label,
    labelClassName,
    onAgingBadgeClick,
    selectedClassName,
}) {
    const isSelected = Array.isArray(agingFilters) && agingFilters.includes(agingType);

    return (
        <button
            type="button"
            onClick={() => onAgingBadgeClick(agingType)}
            aria-pressed={isSelected}
            className={cn(
                'inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                baseClassName,
                hoverClassName,
                focusClassName,
                isSelected && selectedClassName,
            )}
        >
            <Icon className={cn('h-5 w-5', iconClassName)} aria-hidden="true" />
            <span className={cn('text-2xl font-bold', countClassName)}>{count}</span>
            <span className={cn('text-sm font-medium', labelClassName)}>{label}</span>
        </button>
    );
}

/**
 * SummaryAlerts
 *
 * Displays risk-level summary counts for the dashboard.
 * Shows critical, at-risk, and on-track badges alongside
 * total route/shipment/throughput stats.
 *
 * @param {Object} props
 * @param {Object} props.summary - Summary data from the API
 * @param {string} props.statusFilter - Current active status filter
 * @param {Function} props.onStatusFilterClick - Called when a risk badge is clicked
 * @param {string[]} props.agingFilters - Current selected aging filter values
 * @param {Function} props.onAgingBadgeClick - Called when an aging badge is clicked
 */
function SummaryAlerts({ agingFilters = [], onAgingBadgeClick, onStatusFilterClick, statusFilter, summary }) {
    const {
        atRisk = 0,
        critical = 0,
        facilityThroughput = 0,
        labelAgingCount = 0,
        onTrack = 0,
        packingAgingCount = 0,
        totalRoutes = 0,
        totalShipments = 0,
    } = summary || {};

    return (
        <div className="space-y-3">
            {/* Risk Summary Badges */}
            <div className="flex gap-3 flex-wrap">
                <RiskSummaryBadge
                    status={STATUS_FILTER_VALUES.CRITICAL}
                    count={critical}
                    label="Critical Issues"
                    icon={XCircle}
                    statusFilter={statusFilter}
                    onStatusFilterClick={onStatusFilterClick}
                    baseClassName="bg-red-50 border-red-200"
                    selectedClassName="ring-2 ring-red-400 bg-red-100 border-red-300"
                    hoverClassName="hover:bg-red-200"
                    focusClassName="focus-visible:ring-red-400"
                    iconClassName="text-red-500"
                    countClassName="text-red-600"
                    labelClassName="text-red-700"
                />

                <RiskSummaryBadge
                    status={STATUS_FILTER_VALUES.AT_RISK}
                    count={atRisk}
                    label="At Risk"
                    icon={AlertTriangle}
                    statusFilter={statusFilter}
                    onStatusFilterClick={onStatusFilterClick}
                    baseClassName="bg-amber-50 border-amber-200"
                    selectedClassName="ring-2 ring-amber-400 bg-amber-100 border-amber-300"
                    hoverClassName="hover:bg-amber-200"
                    focusClassName="focus-visible:ring-amber-400"
                    iconClassName="text-amber-500"
                    countClassName="text-amber-600"
                    labelClassName="text-amber-700"
                />

                <RiskSummaryBadge
                    status={STATUS_FILTER_VALUES.ON_TRACK}
                    count={onTrack}
                    label="On Track"
                    icon={CheckCircle2}
                    statusFilter={statusFilter}
                    onStatusFilterClick={onStatusFilterClick}
                    baseClassName="bg-green-50 border-green-200"
                    selectedClassName="ring-2 ring-green-400 bg-green-100 border-green-300"
                    hoverClassName="hover:bg-green-200"
                    focusClassName="focus-visible:ring-green-400"
                    iconClassName="text-green-500"
                    countClassName="text-green-600"
                    labelClassName="text-green-700"
                />

                <AgingSummaryBadge
                    agingType={AGING_FILTER_VALUES.LABELS}
                    agingFilters={agingFilters}
                    count={labelAgingCount}
                    label="Label Aging"
                    icon={Clock}
                    onAgingBadgeClick={onAgingBadgeClick}
                    baseClassName="bg-slate-50 border-slate-200"
                    selectedClassName="ring-2 ring-slate-400 bg-slate-100 border-slate-300"
                    hoverClassName="hover:bg-slate-200"
                    focusClassName="focus-visible:ring-slate-400"
                    iconClassName="text-slate-500"
                    countClassName="text-slate-600"
                    labelClassName="text-slate-700"
                />

                <AgingSummaryBadge
                    agingType={AGING_FILTER_VALUES.PACKING}
                    agingFilters={agingFilters}
                    count={packingAgingCount}
                    label="Packing Aging"
                    icon={Clock}
                    onAgingBadgeClick={onAgingBadgeClick}
                    baseClassName="bg-orange-50 border-orange-200"
                    selectedClassName="ring-2 ring-orange-400 bg-orange-100 border-orange-300"
                    hoverClassName="hover:bg-orange-200"
                    focusClassName="focus-visible:ring-orange-400"
                    iconClassName="text-orange-500"
                    countClassName="text-orange-600"
                    labelClassName="text-orange-700"
                />
            </div>

            {/* Stats Summary */}
            <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                    {totalRoutes} routes
                </Badge>
                <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                    {totalShipments} shipments
                </Badge>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className="inline-flex">
                            <Badge variant="outline" className="text-xs font-normal text-muted-foreground gap-1">
                                {facilityThroughput}/hr throughput
                                <Info className="h-3 w-3" />
                            </Badge>
                        </span>
                    </TooltipTrigger>
                    <TooltipContent>
                        Based on the current 2-hour packing rate. Does not reflect historical throughput.
                    </TooltipContent>
                </Tooltip>
            </div>
        </div>
    );
}

export default SummaryAlerts;
